import { BrowserRouter, Routes, Route, Navigate } from 'react-router';
import { useEffect, useState, lazy, Suspense } from 'react';
import type { Session } from '@supabase/supabase-js';
import { supabase } from './lib/supabase.ts';
import { GameLayout }    from './components/GameLayout.tsx';
import { PlanetProvider } from './context/PlanetContext';
import { NotificationsProvider } from './context/NotificationsContext';

const LoginPage          = lazy(() => import('./pages/LoginPage.tsx').then(m => ({ default: m.LoginPage })));
const RegisterPage       = lazy(() => import('./pages/RegisterPage.tsx').then(m => ({ default: m.RegisterPage })));
const OverviewPage       = lazy(() => import('./pages/OverviewPage.tsx').then(m => ({ default: m.OverviewPage })));
const BuildingsPage      = lazy(() => import('./pages/BuildingsPage.tsx').then(m => ({ default: m.BuildingsPage })));
const ResearchPage       = lazy(() => import('./pages/ResearchPage.tsx').then(m => ({ default: m.ResearchPage })));
const ShipyardPage       = lazy(() => import('./pages/ShipyardPage.tsx').then(m => ({ default: m.ShipyardPage })));
const DefensePage        = lazy(() => import('./pages/DefensePage.tsx').then(m => ({ default: m.DefensePage })));
const FleetPage          = lazy(() => import('./pages/FleetPage.tsx').then(m => ({ default: m.FleetPage })));
const GalaxyPage         = lazy(() => import('./pages/GalaxyPage.tsx').then(m => ({ default: m.GalaxyPage })));
const AlliancePage       = lazy(() => import('./pages/AlliancePage.tsx').then(m => ({ default: m.AlliancePage })));
const MessagesPage       = lazy(() => import('./pages/MessagesPage.tsx').then(m => ({ default: m.MessagesPage })));
const HighscorePage      = lazy(() => import('./pages/HighscorePage.tsx').then(m => ({ default: m.HighscorePage })));
const HelpPage           = lazy(() => import('./pages/HelpPage.tsx').then(m => ({ default: m.HelpPage })));
const BrueckePage        = lazy(() => import('./pages/BrueckePage.tsx').then(m => ({ default: m.BrueckePage })));
const AdminPage          = lazy(() => import('./pages/AdminPage.tsx').then(m => ({ default: m.AdminPage })));
const ChatPage           = lazy(() => import('./pages/ChatPage.tsx').then(m => ({ default: m.ChatPage })));
const ResetPasswordPage  = lazy(() => import('./pages/ResetPasswordPage.tsx').then(m => ({ default: m.ResetPasswordPage })));
const AuthCallbackPage   = lazy(() => import('./pages/AuthCallbackPage.tsx').then(m => ({ default: m.AuthCallbackPage })));

function PageFallback() {
  return (
    <div className="flex items-center justify-center flex-1 min-h-32">
      <div className="w-32 h-px bg-space-700 relative overflow-hidden">
        <div className="absolute inset-y-0 w-8 bg-nova-400/50 animate-[shimmer_1.2s_ease-in-out_infinite]" />
      </div>
    </div>
  );
}

export function App() {
  const [session, setSession]       = useState<Session | null | undefined>(undefined);
  const [isRecovery, setIsRecovery] = useState(false);

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => setSession(data.session));
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, s) => {
      if (event === 'PASSWORD_RECOVERY') setIsRecovery(true);
      else setIsRecovery(false);
      setSession(s);
    });
    return () => subscription.unsubscribe();
  }, []);

  if (isRecovery && session) {
    return (
      <BrowserRouter>
        <Suspense fallback={<PageFallback />}>
          <Routes>
            <Route path="*" element={<ResetPasswordPage />} />
          </Routes>
        </Suspense>
      </BrowserRouter>
    );
  }

  if (session === undefined) {
    return (
      <div className="flex items-center justify-center min-h-dvh bg-space-950">
        <div className="text-nova-400 text-xl animate-pulse">Nova Sigma lädt…</div>
      </div>
    );
  }

  return (
    <BrowserRouter>
      <Suspense fallback={<PageFallback />}>
        <Routes>
          {/* Öffentliche Routen */}
          <Route path="/login"          element={!session ? <LoginPage />    : <Navigate to="/" replace />} />
          <Route path="/register"       element={!session ? <RegisterPage /> : <Navigate to="/" replace />} />
          <Route path="/auth/callback"  element={<AuthCallbackPage />} />
          <Route path="/reset-password" element={isRecovery ? <ResetPasswordPage /> : <Navigate to="/" replace />} />

          {/* Geschützte Routen */}
          <Route element={session
            ? <PlanetProvider><NotificationsProvider><GameLayout session={session} /></NotificationsProvider></PlanetProvider>
            : <Navigate to="/login" replace />
          }>
            <Route index                   element={<OverviewPage />} />
            <Route path="buildings"        element={<BuildingsPage />} />
            <Route path="research"         element={<ResearchPage />} />
            <Route path="shipyard"         element={<ShipyardPage />} />
            <Route path="defense"          element={<DefensePage />} />
            <Route path="fleet"            element={<FleetPage />} />
            <Route path="galaxy"           element={<GalaxyPage />} />
            <Route path="alliance"         element={<AlliancePage />} />
            <Route path="messages"         element={<MessagesPage />} />
            <Route path="chat"             element={<ChatPage />} />
            <Route path="highscore"        element={<HighscorePage />} />
            <Route path="help"             element={<HelpPage />} />
            <Route path="bruecke"          element={<BrueckePage />} />
            <Route path="admin"            element={<AdminPage />} />
          </Route>

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
    </BrowserRouter>
  );
}
